import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-8 sm:px-10 sm:py-12">
            <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
            <p className="text-blue-100">Effective Date: September 14, 2025</p>
          </div>
          
          <div className="px-6 py-8 sm:px-10 sm:py-12">
            <div className="prose prose-blue max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">What Information We Collect</h2>
                <p className="text-gray-600 mb-3">We collect information you provide when using our Service:</p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li><strong>Account Information:</strong> Name, email address, company details</li>
                  <li><strong>Video Content:</strong> Traffic footage you upload for analysis</li>
                  <li><strong>Usage Data:</strong> How you interact with our Service</li>
                  <li><strong>Technical Information:</strong> Device and browser information</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
                <p className="text-gray-600 mb-3">We use your information to:</p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Process and analyze your traffic videos</li>
                  <li>Generate Traffic Movement Count reports</li>
                  <li>Improve our Service and develop new features</li>
                  <li>Communicate with you about your account and Service updates</li>
                  <li>Provide customer support</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Protect Your Information</h2>
                <p className="text-gray-600 mb-3">We implement industry-standard security measures:</p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li><strong>Encryption:</strong> All data is encrypted in transit and at rest</li>
                  <li><strong>Access Controls:</strong> Strict access controls limit who can view your data</li>
                  <li><strong>Regular Audits:</strong> Security assessments and penetration testing</li>
                  <li><strong>Employee Training:</strong> Staff trained on privacy and data protection</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Privacy Rights</h2>
                <p className="text-gray-600 mb-3">You have the right to:</p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li><strong>Access:</strong> Request copies of your personal information</li>
                  <li><strong>Correct:</strong> Update inaccurate information</li>
                  <li><strong>Delete:</strong> Request deletion of your data</li>
                  <li><strong>Export:</strong> Download all your data in machine-readable format</li>
                  <li><strong>Object:</strong> Opt out of certain data processing activities</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
                <p className="text-gray-600 mb-3">We automatically delete your data after 90 days unless:</p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  <li>Legal requirements mandate longer retention</li>
                  <li>Data is needed to resolve disputes</li>
                  <li>You request extended retention</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
                <p className="text-gray-600">
                  We use trusted third-party services that help us operate our Service:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-600 mt-3">
                  <li><strong>Amazon Web Services:</strong> Secure cloud infrastructure</li>
                  <li><strong>Stripe:</strong> Payment processing</li>
                  <li><strong>Google Analytics:</strong> Anonymous usage analytics</li>
                </ul>
                <p className="text-gray-600 mt-3">
                  These providers are contractually obligated to protect your information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
                <p className="text-gray-600">
                  Your data may be processed in countries with different privacy laws. We ensure appropriate safeguards are in place for international transfers.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
                <p className="text-gray-600">
                  Our Service is not intended for children under 16. We do not knowingly collect information from children.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Updates to This Policy</h2>
                <p className="text-gray-600">
                  We may update this policy from time to time. We'll notify you of significant changes via email or in the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
                <p className="text-gray-600 mb-3">For privacy questions or requests:</p>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-gray-700"><strong>Email:</strong> <a href="mailto:info@neuralvision.ca" className="text-blue-600 hover:text-blue-800">info@neuralvision.ca</a></p>
                  <p className="text-gray-700"><strong>Phone:</strong> 1-800-NEURAL-VIS</p>
                </div>
                <p className="text-gray-600 mt-3">
                  You can also contact your local privacy regulator if you have concerns about our privacy practices.
                </p>
              </section>

              <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      <strong>Compliance:</strong> This Privacy Policy complies with PIPEDA (Canada), GDPR (EU), and CCPA (California) requirements.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-gray-200">
              <Link 
                to="/dashboard" 
                className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;